import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { CreateJustificationTemplateUseCase } from "@project/domain/usecases/budget/create-justification-template.usecase";
import { FetchJustificationTemplatesUseCase } from "@project/domain/usecases/budget/fetch-justification-templates-usecase";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";
// import { Http } from "@core/infrastructure/http/http";

export interface JustificationTemplateBaseOption {
    id: number;
    name: string;
    description: string;
    version: number;
    json_schema?: any;
    template_key: string;
}

@injectable()
export class JustificationTemplateCreatePageViewModel extends BaseViewModel {
    @observable templates: JustificationTemplateBaseOption[] = [];
    @observable loadingTemplates = false;
    @observable creating = false;

    constructor(
        @inject(FetchJustificationTemplatesUseCase)
        private readonly fetchJustificationTemplatesUseCase: FetchJustificationTemplatesUseCase,
        @inject(CreateJustificationTemplateUseCase)
        private readonly createJustificationTemplateUseCase: CreateJustificationTemplateUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    async fetchTemplates() {
        this.loadingTemplates = true;
        try {
            const templatesRaw =
                await this.fetchJustificationTemplatesUseCase.execute();
            // Mapear para asegurar que template_key está presente
            const templates = templatesRaw.map((tpl: any) => ({
                id: tpl.id,
                name: tpl.name,
                description: tpl.description,
                version: tpl.version,
                json_schema: tpl.json_schema,
                template_key: tpl.template_key,
            }));
            runInAction(() => {
                this.templates = templates;
            });
        } catch (e) {
            ToastManagerStore.error("Error cargando plantillas base");
        } finally {
            runInAction(() => {
                this.loadingTemplates = false;
            });
        }
    }

    @action
    async createTemplate(values: any): Promise<number | null> {
        this.creating = true;
        try {
            // Buscar la plantilla base seleccionada para clonar su json_schema y template_key
            const base = this.templates.find(
                (t) => t.id === values.baseTemplateId,
            );
            if (!base) {
                ToastManagerStore.error("Plantilla base no encontrada");
                return null;
            }
            // Copiar json_schema y template_key de la base y añadir sufijo UUID a template_key
            const uuid = crypto.randomUUID
                ? crypto.randomUUID()
                : Math.random().toString(36).substring(2, 10) + Date.now();
            const generatedTemplateKey = `${base.template_key}_${uuid}`;
            // Clonar el json_schema para no mutar el original
            const jsonSchema = { ...(base.json_schema || {}) };
            // Modificar los campos requeridos
            jsonSchema.fileName = `${values.name.replace(/\s+/g, "_")}.xls`;
            jsonSchema.description = values.description || "";
            jsonSchema.templateKey = generatedTemplateKey;
            const input = {
                name: values.name,
                description: values.description,
                version: values.version,
                visible: true,
                json_schema: jsonSchema,
                template_key: generatedTemplateKey,
            };
            const id =
                await this.createJustificationTemplateUseCase.execute(input);
            if (id) {
                ToastManagerStore.success("Plantilla creada correctamente");
                return id;
            } else {
                ToastManagerStore.error("Error creando la plantilla");
                return null;
            }
        } catch (e) {
            ToastManagerStore.error("Error creando la plantilla");
            return null;
        } finally {
            runInAction(() => {
                this.creating = false;
            });
        }
    }
}
